import React, { useState, useCallback } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Capacitor } from '@capacitor/core';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import SettingRow from '../SettingRow/SettingRow';
import Backend from '../../../backend';
import { toast } from 'react-toastify';
import IconToast from '../../../components/NewToast';
import _ from 'lodash';

const AppleBindSetting = ({ intl, userDetails, updateUserDetails }) => {
  const [bindLoading, setBindLoading] = useState(false);

  const isBound = !!_.get(userDetails, 'appleUserId');

  const handleUnbind = useCallback(async () => {
    try {
      const res = await fetch(Backend.api('unbindApple'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      const payload = await res.json();
      if (payload.success) {
        toast.info(IconToast('success', intl.formatMessage({ id: 'apple-unbound-successfully' })), {
          className: 'new-toast'
        });
        updateUserDetails({ appleUserId: null });
      } else {
        toast.info(IconToast('error', intl.formatMessage({ id: 'apple-unbind-failed' })), { className: 'new-toast' });
      }
    } catch (error) {
      console.error('Apple unbind error:', error);
      toast.info(IconToast('error', intl.formatMessage({ id: 'apple-unbind-failed' })), { className: 'new-toast' });
    }
  }, [intl, updateUserDetails]);

  const handleBind = useCallback(async () => {
    try {
      setBindLoading(true);
      const isWeb = Capacitor.getPlatform() === 'web';
      const result = await SignInWithApple.authorize({
        clientId: isWeb ? 'com.portfoplus.web' : 'com.portfoplus.PortfoPlus',
        redirectURI: `${process.env.PFPLUS_URL}/login`,
        scopes: 'email name'
      });

      const { identityToken } = result.response;
      if (!identityToken) {
        toast.info(IconToast('error', intl.formatMessage({ id: 'apple-authentication-failed' })), {
          className: 'new-toast'
        });
        setBindLoading(false);
        return;
      }

      const res = await fetch(Backend.api('bindApple'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ appleIdToken: identityToken })
      });
      const payload = await res.json();
      if (payload.success) {
        toast.info(IconToast('success', intl.formatMessage({ id: 'apple-bound-successfully' })), {
          className: 'new-toast'
        });
        updateUserDetails({ appleUserId: payload.appleUserId });
      } else {
        toast.info(IconToast('error', intl.formatMessage({ id: payload.message || 'apple-bind-failed' })), {
          className: 'new-toast'
        });
      }
    } catch (error) {
      console.error('Apple bind error:', error);
      toast.info(IconToast('error', intl.formatMessage({ id: 'apple-bind-failed' })), { className: 'new-toast' });
    } finally {
      setBindLoading(false);
    }
  }, [intl, updateUserDetails]);

  if (Capacitor.getPlatform() === 'android') {
    return null;
  }

  return (
    <SettingRow
      label={intl.formatMessage({ id: 'apple-account' })}
      value={
        isBound ? (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
            <CheckCircleIcon sx={{ fontSize: 18 }} />
            {intl.formatMessage({ id: 'bound' })}
          </Box>
        ) : (
          intl.formatMessage({ id: 'not-bound' })
        )
      }
      action={
        <Button
          variant={isBound ? 'text' : 'outlined'}
          size="small"
          onClick={isBound ? handleUnbind : handleBind}
          color={isBound ? 'inherit' : 'primary'}
          disabled={bindLoading}
          sx={{ minWidth: 42, fontSize: 12, px: 1, py: 0.25, color: isBound ? 'text.secondary' : undefined }}
        >
          {bindLoading ? <CircularProgress size={16} /> : intl.formatMessage({ id: isBound ? 'unbind' : 'bind' })}
        </Button>
      }
    />
  );
};

export default AppleBindSetting;
